/**
 * Home Page Component
 * Modern single-page scroll layout matching inspiration patterns
 */

import styles from './HomePage.module.css';
import { Button } from '../../components/Button/Button.js';
import { QuickLinks } from '../../components/QuickLinks/QuickLinks.js';
import { ProgramCard } from '../../components/ProgramCard/ProgramCard.js';
import { FacultyCard } from '../../components/FacultyCard/FacultyCard.js';
import { Schedule } from '../../components/Schedule/Schedule.js';
import { TestimonialsCarousel } from '../../components/TestimonialsCarousel/TestimonialsCarousel.js';
import { Section } from '../../components/Section/Section.js';

export class HomePage {
  render() {
    // Use a wrapper div instead of fragment for easier debugging
    const wrapper = document.createElement('div');
    wrapper.className = 'homepage-wrapper';

    try {
      // Hero Section
      const hero = this.createHero();
      wrapper.appendChild(hero);
      console.log('Hero added');

      // Quick Links
      const quickLinks = new QuickLinks();
      wrapper.appendChild(quickLinks.render());
      console.log('QuickLinks added');

      // About Section
      const about = this.createAboutSection();
      wrapper.appendChild(about);
      console.log('About added');

      // Programs Section
      const programs = this.createProgramsSection();
      wrapper.appendChild(programs);
      console.log('Programs added');

      // Nutcracker 2025 Featured Section
      const nutcracker = this.createNutcrackerSection();
      wrapper.appendChild(nutcracker);
      console.log('Nutcracker added');

      // Schedule Section
      const schedule = new Schedule();
      wrapper.appendChild(schedule.render());
      console.log('Schedule added');

      // Faculty Section
      const faculty = this.createFacultySection();
      wrapper.appendChild(faculty);
      console.log('Faculty added');

      // Testimonials Section
      const testimonials = new TestimonialsCarousel();
      wrapper.appendChild(testimonials.render());
      console.log('Testimonials added');
    } catch (error) {
      console.error('Error rendering HomePage:', error);
      // Add error message to page
      const errorDiv = document.createElement('div');
      errorDiv.style.padding = '20px';
      errorDiv.style.color = 'red';
      errorDiv.textContent = `Error: ${error.message}`;
      wrapper.appendChild(errorDiv);
    }

    return wrapper;
  }

  createHero() {
    const hero = document.createElement('section');
    hero.className = styles.hero;
    hero.setAttribute('id', 'home');

    const overlay = document.createElement('div');
    overlay.className = styles.heroOverlay;

    const content = document.createElement('div');
    content.className = styles.heroContent;

    const heading = document.createElement('h1');
    heading.className = styles.heroHeading;
    heading.textContent = 'Classical Ballet of California';

    const subheading = document.createElement('p');
    subheading.className = styles.heroSubheading;
    subheading.textContent = 'Providing exceptional ballet tuition for all ages (3 - Adult) in a convenient location 15 minutes from San Francisco. Founded in 1997.';

    const ctaContainer = document.createElement('div');
    ctaContainer.className = styles.heroCTA;

    const primaryButton = new Button({
      text: 'Enroll Now',
      href: '/join',
      variant: 'primary'
    });

    const secondaryButton = new Button({
      text: 'View Schedule',
      href: '#schedule',
      variant: 'white'
    });

    content.appendChild(heading);
    content.appendChild(subheading);
    ctaContainer.appendChild(primaryButton.render());
    ctaContainer.appendChild(secondaryButton.render());
    content.appendChild(ctaContainer);

    hero.appendChild(overlay);
    hero.appendChild(content);

    return hero;
  }

  createAboutSection() {
    const section = new Section({
      id: 'about',
      title: 'About Classical Ballet of California',
      subtitle: 'Dedicated to excellence in ballet education',
      backgroundColor: 'default'
    });

    const content = document.createElement('div');
    content.className = styles.aboutContent;

    const text = document.createElement('div');
    text.className = styles.aboutText;

    const p1 = document.createElement('p');
    p1.textContent = 'Classical Ballet of California is a premier ballet school located in Pacifica, California, just 15 minutes from San Francisco. Founded in 1997 by Director Nina Baratova, we offer comprehensive ballet training for students of all ages and skill levels, from young children taking their first steps in dance to adults pursuing their passion for ballet.';
    text.appendChild(p1);

    const p2 = document.createElement('p');
    p2.textContent = 'Our experienced faculty, led by former Sacramento Ballet Principal Nina Baratova and co-director Gregory Amato, is dedicated to providing exceptional instruction in a supportive and nurturing environment. We believe in the power of classical ballet to develop discipline, artistry, and confidence in our students.';
    text.appendChild(p2);

    const features = document.createElement('div');
    features.className = styles.aboutFeatures;

    const featureList = [
      { icon: '🎓', title: 'Expert Faculty', desc: 'Learn from former professional dancers including Sacramento Ballet Principal' },
      { icon: '👥', title: 'All Ages Welcome', desc: 'Programs for ages 3 to adult, from beginner to professional levels' },
      { icon: '📍', title: 'Convenient Location', desc: '1919 Palmetto Ave, Pacifica - just 15 minutes from San Francisco' },
      { icon: '🎭', title: 'Performance Opportunities', desc: 'Annual Nutcracker with 200+ dancers and other productions' }
    ];

    featureList.forEach(feature => {
      const featureCard = document.createElement('div');
      featureCard.className = styles.featureCard;

      const icon = document.createElement('div');
      icon.className = styles.featureIcon;
      icon.textContent = feature.icon;

      const info = document.createElement('div');
      info.className = styles.featureInfo;

      const title = document.createElement('h4');
      title.textContent = feature.title;
      info.appendChild(title);

      const desc = document.createElement('p');
      desc.textContent = feature.desc;
      info.appendChild(desc);

      featureCard.appendChild(icon);
      featureCard.appendChild(info);
      features.appendChild(featureCard);
    });

    content.appendChild(text);
    content.appendChild(features);

    // Add content to section
    const sectionElement = section.render();
    // Find the container div (it's a direct child of the section)
    const container = sectionElement.querySelector('div');
    if (container) {
      container.appendChild(content);
    }

    return sectionElement;
  }

  createProgramsSection() {
    const section = new Section({
      id: 'programs',
      title: 'Our Programs',
      subtitle: 'Find the perfect class for every age and skill level',
      backgroundColor: 'alt'
    });

    const programsGrid = document.createElement('div');
    programsGrid.className = styles.programsGrid;

    const programs = [
      {
        title: 'Pre-Ballet',
        description: 'Introduction to movement and music in a fun, creative environment for young dancers',
        ageGroup: 'Ages 3-5',
        image: '/placeholder-program.jpg',
        href: '/programs/pre-ballet'
      },
      {
        title: 'Level 1',
        description: 'Foundational ballet technique and terminology for beginning students',
        ageGroup: 'Ages 6+',
        image: '/placeholder-program.jpg',
        href: '/programs/level-1'
      },
      {
        title: 'Level 2-3',
        description: 'Intermediate technique building on fundamental skills',
        ageGroup: 'Ages 9+',
        image: '/placeholder-program.jpg',
        href: '/programs/level-2-3'
      },
      {
        title: 'Advanced',
        description: 'Advanced training with focus on performance and artistry',
        ageGroup: 'Ages 13+',
        image: '/placeholder-program.jpg',
        href: '/programs/advanced'
      },
      {
        title: 'Adult Ballet',
        description: 'Classes for adults of all levels, from beginners to advanced',
        ageGroup: 'Adults',
        image: '/placeholder-program.jpg',
        href: '/programs/adult-ballet'
      },
      {
        title: 'Contemporary',
        description: 'Contemporary dance class taught by Aline Carili on Fridays',
        ageGroup: 'All Levels',
        image: '/placeholder-program.jpg',
        href: '/programs/contemporary',
        featured: true
      }
    ];

    programs.forEach(program => {
      const card = new ProgramCard(program);
      programsGrid.appendChild(card.render());
    });

    const sectionElement = section.render();
    // Find the container div (it's a direct child of the section)
    const container = sectionElement.querySelector('div');
    if (container) {
      container.appendChild(programsGrid);
    }

    return sectionElement;
  }

  createNutcrackerSection() {
    const section = document.createElement('section');
    section.className = styles.nutcrackerSection;
    section.setAttribute('id', 'nutcracker');

    const container = document.createElement('div');
    container.className = styles.nutcrackerContainer;

    const content = document.createElement('div');
    content.className = styles.nutcrackerContent;

    const badge = document.createElement('span');
    badge.className = styles.nutcrackerBadge;
    badge.textContent = 'Featured Event';
    content.appendChild(badge);

    const title = document.createElement('h2');
    title.className = styles.nutcrackerTitle;
    title.textContent = 'Nutcracker 2025';
    content.appendChild(title);

    const description = document.createElement('p');
    description.className = styles.nutcrackerDescription;
    description.textContent = 'Come see a spectacular production of "The Nutcracker" with a cast of over 200 local dancers from ages 3 - adult. Give your loved ones the gift of dance this holiday season!';
    content.appendChild(description);

    const info = document.createElement('div');
    info.className = styles.nutcrackerInfo;

    const date = document.createElement('div');
    date.className = styles.nutcrackerDate;
    date.innerHTML = '<strong>Performance Dates:</strong><br>Saturday, December 14 @ 11:00 AM & 2:00 PM<br>Sunday, December 15 @ 11:00 AM & 2:00 PM';
    info.appendChild(date);

    const cta = new Button({
      text: 'Learn More',
      href: '/nutcracker',
      variant: 'white'
    });
    content.appendChild(cta.render());

    container.appendChild(content);

    const image = document.createElement('div');
    image.className = styles.nutcrackerImage;
    // Placeholder for Nutcracker image
    image.setAttribute('aria-label', 'Nutcracker 2025 performance');

    container.appendChild(image);
    section.appendChild(container);

    return section;
  }

  createFacultySection() {
    const section = new Section({
      id: 'faculty',
      title: 'Our Faculty',
      subtitle: 'Meet our experienced and dedicated instructors',
      backgroundColor: 'default'
    });

    const facultyGrid = document.createElement('div');
    facultyGrid.className = styles.facultyGrid;

    const faculty = [
      {
        name: 'Nina Baratova',
        title: 'Director & Founder',
        bio: 'Former principal with Sacramento Ballet. Trained at San Francisco Ballet School and Bolshoi Ballet Academy. Finalist in 5th International Ballet Competition in Paris. Director since 1997.',
        image: '/placeholder-faculty.jpg',
        email: 'classicalballetofCA@gmail.com'
      },
      {
        name: 'Gregory Amato',
        title: 'Co-Director',
        bio: 'Extensive professional career including Miami City Ballet, Smuin Ballet S.F., and Ballet Du Nord in France. Danced in 53 countries and performed at Carnegie Hall.',
        image: '/placeholder-faculty.jpg',
        email: 'classicalballetofCA@gmail.com'
      },
      {
        name: 'Aline Carili',
        title: 'Contemporary Instructor',
        bio: 'Trained at Mvsika Centro de Estudos in Brazil. Currently a member of Peninsula Ballet Theatre. Teaches Contemporary class on Fridays.',
        image: '/placeholder-faculty.jpg',
        email: 'classicalballetofCA@gmail.com'
      },
      {
        name: 'Minna Rogers',
        title: 'Multi-Discipline Instructor',
        bio: 'Professionally trained in Ballet, Jazz, Tap, and Modern Dance. Integrates technical excellence with emotional expression. Also choreographs and performs locally.',
        image: '/placeholder-faculty.jpg',
        email: 'classicalballetofCA@gmail.com'
      }
    ];

    faculty.forEach(member => {
      const card = new FacultyCard(member);
      facultyGrid.appendChild(card.render());
    });

    const sectionElement = section.render();
    // Find the container div (it's a direct child of the section)
    const container = sectionElement.querySelector('div');
    if (container) {
      container.appendChild(facultyGrid);
    }

    return sectionElement;
  }
}
